// Iter55 — Compact "Next up" widget for the home Dashboard.
//
// Consumes `/api/countdown/next-24h?limit=3&horizon_hours=24` and renders the
// merged top-3 (My tasks + Connected). Live 30s refresh of the countdown
// timer. Rows click through to the underlying SR / incident / clean-point
// SR. Empty state is calm green ("Nothing urgent in the next 24h ✓"), not
// an error.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Clock, AlertCircle, Network, PackageSearch, ArrowRight, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

const SEV_COLOR = { s1: "#E24B4A", s2: "#f3b14a", s3: "#4ed084", s4: "#9aa1b3" };

const KIND_META = {
  incident_sla: { icon: AlertCircle, label: "Incident", color: "#9ec3ec" },
  service_request_due: { icon: Network, label: "SR", color: "#c89adb" },
  clean_point_plus_one: { icon: PackageSearch, label: "Clean point", color: "#f3b14a" },
};

const fmtCountdown = (deadlineIso, nowMs) => {
  const t = new Date(deadlineIso).getTime() - nowMs;
  const abs = Math.abs(t);
  const sign = t < 0 ? "-" : "";
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  if (h >= 24) return `${sign}${Math.floor(h / 24)}d ${h % 24}h`;
  if (h >= 1) return `${sign}${h}h ${m}m`;
  return `${sign}${m}m`;
};

const urgencyColor = (deadlineIso, nowMs) => {
  const dt = new Date(deadlineIso).getTime() - nowMs;
  if (dt < 0) return "#E24B4A";
  if (dt < 30 * 60_000) return "#E24B4A";
  if (dt < 2 * 3_600_000) return "#f3b14a";
  return "#9aebc2";
};

export const NextUpWidget = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/countdown/next-24h?horizon_hours=24&limit=3`);
      setItems(r.data?.top_urgent || []);
      setErr(false);
    } catch (_) {
      // Distinguish error from loading so we don't get stuck on "Loading…"
      setItems((prev) => (prev === null ? [] : prev));
      setErr(true);
    }
  };

  useEffect(() => {
    refresh();
    const tick = setInterval(refresh, 60_000);
    const cd = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => { clearInterval(tick); clearInterval(cd); };
  }, []);

  return (
    <div
      className="rounded-md p-4 flex flex-col"
      style={{
        background: "#111420",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
      data-testid="dashboard-next-up"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock size={13} style={{ color: "#9ec3ec" }} />
          <span style={{ ...mono(9, "#9198b0", 700, "1.4px"), textTransform: "uppercase" }}>
            Next up · 24h
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/countdown")}
          className="px-2 py-0.5 rounded"
          style={{
            background: "transparent",
            border: "1px solid rgba(24,95,165,0.45)",
            color: "#9ec3ec",
            ...mono(9, undefined, 700, "0.4px"),
            textTransform: "uppercase",
            cursor: "pointer",
          }}
          data-testid="dashboard-next-up-view-all"
        >View all →</button>
      </div>

      {items === null ? (
        <div className="flex-1 flex items-center justify-center" style={mono(11, "#9aa1b3")} data-testid="dashboard-next-up-loading">
          Loading…
        </div>
      ) : err && items.length === 0 ? (
        <div
          className="flex-1 flex items-center justify-center text-center px-3 py-4 rounded"
          style={{ background: "rgba(226,75,74,0.06)", border: "1px dashed rgba(226,75,74,0.30)", ...mono(11, "#ff9a99") }}
          data-testid="dashboard-next-up-error"
        >Couldn't load — will retry</div>
      ) : items.length === 0 ? (
        <div
          className="flex-1 flex items-center justify-center text-center px-3 py-4 rounded"
          style={{
            background: "rgba(78,208,132,0.06)",
            border: "1px dashed rgba(78,208,132,0.30)",
          }}
          data-testid="dashboard-next-up-empty"
        >
          <span style={mono(11, "#9aebc2", 600)} className="flex items-center gap-1.5">
            <CheckCircle2 size={14} />
            Nothing urgent in the next 24h
          </span>
        </div>
      ) : (
        <div className="space-y-1.5 flex-1" data-testid="dashboard-next-up-list">
          {items.map((it) => (
            <Row key={it.id} item={it} nowMs={nowMs} onClick={() => navigate(it.link)} />
          ))}
        </div>
      )}
    </div>
  );
};

const Row = ({ item, nowMs, onClick }) => {
  const meta = KIND_META[item.kind] || { icon: Clock, label: item.kind, color: "#9aa1b3" };
  const Icon = meta.icon;
  const cd = fmtCountdown(item.deadline_at, nowMs);
  const c = urgencyColor(item.deadline_at, nowMs);
  const overdue = new Date(item.deadline_at).getTime() < nowMs;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded p-2 flex items-center gap-2"
      style={{
        background: "#0d0f18",
        border: `1px solid ${overdue ? "rgba(226,75,74,0.40)" : "rgba(255,255,255,0.06)"}`,
        cursor: "pointer",
      }}
      data-testid={`dashboard-next-up-item-${item.id}`}
      data-overdue={overdue ? "true" : "false"}
    >
      <span style={{
        width: 22, height: 22, borderRadius: 4, flexShrink: 0,
        background: `${meta.color}1a`,
        border: `1px solid ${meta.color}55`,
        color: meta.color,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={11} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 11,
          color: "var(--text, #f0f2f8)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{item.title}</div>
        <div style={mono(8, "#9198b0", undefined, "0.4px")}>
          <span style={{ color: meta.color, fontWeight: 700 }}>{meta.label}</span>
          {" · "}{item.ref_id}
          {item.severity && (
            <span style={{ marginLeft: 4, color: SEV_COLOR[item.severity] || "#9aa1b3", fontWeight: 700, textTransform: "uppercase" }}>
              {" · "}{item.severity}
            </span>
          )}
        </div>
      </div>
      <div className="text-right" style={{ flexShrink: 0 }}>
        <span style={mono(11, c, 700, "0.4px")} data-testid={`dashboard-next-up-item-${item.id}-cd`}>
          {overdue ? `-${cd.replace("-", "")}` : cd}
        </span>
      </div>
      <ArrowRight size={11} style={{ color: "#9198b0", flexShrink: 0 }} />
    </button>
  );
};
